import { Modal } from '../modal/Modal';

import { createUserWithEmailAndPassword, getAuth, GoogleAuthProvider, sendEmailVerification, sendPasswordResetEmail, signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { useDispatch, useSelector } from 'react-redux';
import { StyledLoginModal } from './login-modal-style';
import { useEffect, useState } from 'react';
import { setShowLoginModalAction } from '../../store/actions/ui';
import { Button } from '../button/Button';
import { TextInput } from '../text-input/TextInput';
import googleLogo from '../../images/google.png';
import { Spacer } from '../Spacer';
import { setErrorAction, setLoadingAction, setSuccessAction } from '../../store/actions/ui';
import { isValidEmail } from '../../utils/emails';
import { RootState } from '../../store/store';
import { Link } from 'react-router-dom';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../../fire';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { Trans, useTranslation } from 'react-i18next';
import { getLocalizedPath } from '../../utils/lang';

type IStep = "select" | "accountQuestion" | "login" | "signup" | "forget" | "verify";

export const LoginModal = () => {

  const dispatch = useDispatch();
  const auth = getAuth();
  const showLoginModal = useSelector((state: RootState) => state.ui.showLoginModal);
  const [step, setStep] = useState<IStep>("select");
  const { t } = useTranslation();

  const handleClose = () => {
    dispatch(setShowLoginModalAction(false));
  }

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  const [newEmail, setNewEmail] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [newRepeatPassword, setNewRepeatPassword] = useState<string>("");
  const [forgetEmail, setForgetEmail] = useState<string>("");

  const handeClickSigninWithGoole = () => {
    const provider = new GoogleAuthProvider();
    dispatch(setLoadingAction(true));
    signInWithPopup(auth, provider)
      .then(() => {
        logEvent(analytics, 'login', { method: "Google" });
        dispatch(setLoadingAction(false));
        handleClose();
      }).catch((error) => {
        dispatch(setLoadingAction(false));
        dispatch(setErrorAction(error.message));
      });
  }

  const handleClickCreateWithEmail = () => {
    if (newEmail) {
      if (isValidEmail(newEmail)) {
        if (newPassword === newRepeatPassword) {
          const auth = getAuth();
          dispatch(setLoadingAction(true));
          createUserWithEmailAndPassword(auth, newEmail, newPassword)
            .then(() => {
              if (auth?.currentUser) {
                logEvent(analytics, 'sign_up', { method: "Email" });
                sendEmailVerification(auth.currentUser)
                  .then(() => {
                    dispatch(setLoadingAction(false));
                    setStep('verify');
                  });
              }
            })
            .catch((error) => {
              dispatch(setLoadingAction(false));
              dispatch(setErrorAction(error.message));
            });
        } else {
          dispatch(setErrorAction("Passwords does not match. Retry"));
        }
      } else {
        dispatch(setLoadingAction(true));
        setTimeout(() => {
          dispatch(setLoadingAction(false));
          dispatch(setErrorAction("Email not valid. Retry"));
        }, 5000);
      }
    } else {
      dispatch(setErrorAction("Missing email"));
    }
  };

  useEffect(() => {
    if (!showLoginModal) {
      setStep("select");
    }
  }, [showLoginModal]);

  const handeClickSigninWithEmail = () => {
    dispatch(setLoadingAction(true));
    signInWithEmailAndPassword(auth, email, password)
      .then(() => {
        logEvent(analytics, 'login', { method: "Email" });
        dispatch(setLoadingAction(false));
        handleClose();
      })
      .catch((error) => {
        dispatch(setLoadingAction(false));
        dispatch(setErrorAction(error.message));
      });
  };

  const handleClickForget = () => {
    const auth = getAuth();
    dispatch(setLoadingAction(true));
    sendPasswordResetEmail(auth, email)
      .then(() => {
        dispatch(setLoadingAction(false));
        dispatch(setSuccessAction("We sent a password reset email. Check your email"));
        setStep("login");
      })
      .catch((error) => {
        dispatch(setLoadingAction(false));
        dispatch(setErrorAction(error.message));
      });

  };

  const handleClickVerified = () => {
    const auth = getAuth();
    auth.currentUser?.reload().then(() => {
      if (auth.currentUser?.emailVerified) {
        handleClose();
      } else {
        dispatch(setErrorAction(`Your email is not verified. We have sent a verification email to ${auth.currentUser?.email}. Before continuing click on the link and refresh the page. If you can't find the email, check your spam.`))
      }
    });
  };

  return (
    <Modal show={showLoginModal} onClose={handleClose}>
      <StyledLoginModal className='LoginModal'>

        {(step !== 'verify') && (
          <div className='titles'>
            <h1>{t('loginmodal_welcome')}</h1>
            <p><Trans i18nKey="loginmodal_need_account" components={{ b: <b /> }} /></p>
          </div>
        )}

        {(step === 'select') && (
          <div className='select'>

            <Button onClick={handeClickSigninWithGoole}>
              <img src={googleLogo} alt='google logo' />
              <span>{t('loginmodal_continue_google')}</span>
            </Button>

            <Spacer />

            <Button icon={faEnvelope} onClick={() => setStep('accountQuestion')}>
              <span data-testid="continue-with-email">{t('loginmodal_continue_email')}</span>
            </Button>

            <Spacer />

            <p className='disclaimer'>
              {t('loginmodal_disclaimer', { terms: <Link onClick={handleClose} to={getLocalizedPath("/terms")}>{t('loginmodal_terms')}</Link>, privacy: <Link onClick={handleClose} to={getLocalizedPath("/privacy")}>{t('loginmodal_privacy')}</Link> })}
            </p>

          </div>
        )}

        {(step === 'accountQuestion') && (
          <div className='accountQuestion'>
            <p><b>{t('loginmodal_have_account')}</b></p>
            <Spacer />

            <div>
              <Button onClick={() => setStep("login")}>                
                <span data-testid="yes-i-have-an-account-button">{t('loginmodal_yes_account')}</span>
              </Button>

              <Spacer />

              <Button onClick={() => setStep("signup")}>                
                <span>{t('loginmodal_no_account')}</span>
              </Button>
            </div>
          </div>
        )}

        {(step === 'login') && (
          <div className='login'>
            <TextInput type='email' value={email} onChange={(e: any) => setEmail(e.target.value)} placeholder={t('loginmodal_email_placeholder')} />
            <TextInput type='password' value={password} onChange={(e: any) => setPassword(e.target.value)} placeholder={t('loginmodal_password_placeholder')} />

            <button className='forget' onClick={() => setStep("forget")}>              
              <span>{t('loginmodal_forgot_password')}</span>
            </button>

            <div className='buttons'>
              <Button onClick={handeClickSigninWithEmail}>
                <span data-testid="login-button">{t('loginmodal_login')}</span>
              </Button>
              <Button variant='secondary' onClick={() => setStep("select")}>                
                <span>{t('loginmodal_back')}</span>
              </Button>
            </div>
          </div>
        )}

        {(step === 'signup') && (
          <div className='signup'>
            <p><b>{t('loginmodal_create_account')}</b></p>
            <Spacer />

            <div className='inputs'>
              <TextInput value={newEmail} onChange={(e: any) => setNewEmail(e.target.value)} placeholder={t('loginmodal_email_placeholder')} />
              <TextInput type='password' value={newPassword} onChange={(e: any) => setNewPassword(e.target.value)} placeholder={t('loginmodal_password_placeholder')} />
              <TextInput type='password' value={newRepeatPassword} onChange={(e: any) => setNewRepeatPassword(e.target.value)} placeholder={t('loginmodal_repeat_password_placeholder')} />
            </div>

            <div className='buttons'>
              <Button onClick={handleClickCreateWithEmail}>
                <span>{t('loginmodal_enter')}</span>
              </Button>
              <Button variant='secondary' onClick={() => setStep("select")}>                
                <span>{t('loginmodal_back')}</span>
              </Button>
            </div>
          </div>
        )}

        {(step === 'forget') && (
          <div className='forget'>
            <p><b>{t('loginmodal_write_email')}</b></p>
            <Spacer />
            <TextInput value={forgetEmail} onChange={(e: any) => setForgetEmail(e.target.value)} placeholder={t('loginmodal_email_placeholder')} />
            <Spacer />
            <div className='buttons'>
              <Button onClick={handleClickForget}>
                <span>{t('loginmodal_send_email')}</span>
              </Button>
              <Button variant='secondary' onClick={() => setStep("login")}>                
                <span>{t('loginmodal_back')}</span>
              </Button>
            </div>
          </div>
        )}

        {(step === 'verify') && (
          <div className='verify'>
            <p><Trans i18nKey="loginmodal_verification_sent" values={{ email: newEmail }} components={{ b: <b /> }} /></p>
            <Spacer />
            <div className='buttons'>
              <Button onClick={handleClickVerified}>
                <span>{t('loginmodal_verified')}</span>
              </Button>
            </div>
          </div>
        )}

      </StyledLoginModal>
    </Modal>
  );
};